import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, ArrowUpRight, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageContainer, PageHeader, Segmented, Skeleton, EmptyState, InlineError, SearchField, CompanyLogo } from "@/components/common";
import { ConfidenceChip, ScorePill, SourceLine, TimingChip, phaseLabel, ValueRange, EvidenceLabel } from "@/components/intel/primitives";
import { useIntel } from "@/components/intel/context";
import { useMediaQuery } from "@/hooks/use-media-query";

const DAYS = [{ key: "0", label: "All time" }, { key: "7", label: "7 days" }, { key: "14", label: "14 days" }, { key: "30", label: "30 days" }];

export default function IntelRadarPage() {
  const navigate = useNavigate();
  const intel = useIntel();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [country, setCountry] = useState("all");
  const [confidence, setConfidence] = useState("all");
  const [days, setDays] = useState("0");
  const [selected, setSelected] = useState(null);

  const load = () => { setError(null); api.get("/intel/radar").then((r) => setData(r.data)).catch((e) => setError(errMsg(e))); };
  useEffect(() => { load(); }, [intel.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    let r = data?.rows || [];
    if (type !== "all") r = r.filter((x) => x.signal.type === type);
    if (country !== "all") r = r.filter((x) => x.company.country === country);
    if (confidence !== "all") r = r.filter((x) => x.confidence === confidence);
    if (days !== "0") r = r.filter((x) => x.days_ago <= Number(days));
    const s = q.trim().toLowerCase();
    if (s) r = r.filter((x) => `${x.signal.title} ${x.company.name} ${x.project_name} ${x.company.city} ${x.company.segment}`.toLowerCase().includes(s));
    return r;
  }, [data, type, country, confidence, days, q]);

  const countries = useMemo(() => Array.from(new Set((data?.rows || []).map((r) => r.company.country))).sort(), [data]);
  const types = data?.signal_types || {};
  const active = [type !== "all" && { k: "type", label: types[type] || type, clear: () => setType("all") }, country !== "all" && { k: "country", label: country, clear: () => setCountry("all") }, confidence !== "all" && { k: "confidence", label: `${confidence} confidence`, clear: () => setConfidence("all") }, days !== "0" && { k: "days", label: `last ${days} days`, clear: () => setDays("0") }].filter(Boolean);

  const open = (r) => { setSelected(r.signal.id); intel.openSignal({ project_id: r.project_id, company: r.company, project_name: r.project_name }); };

  return (
    <PageContainer wide data-testid="intel-radar-page">
      <PageHeader
        eyebrow="Sales Intelligence"
        title="Project Radar"
        description="Every detected signal with its source, confidence and the project it points to. Click a row for the full evidence."
        actions={data ? <span className="text-[12.5px] text-muted-foreground" data-testid="radar-result-count"><span className="font-semibold tabular text-foreground">{rows.length}</span> of {data.total} signals</span> : null}
      />
      {error ? <InlineError onRetry={load} testId="radar-error">{error}</InlineError> : null}

      <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-9 w-[200px] rounded-[10px] bg-white" data-testid="radar-type-filter"><SelectValue placeholder="Signal type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All signal types</SelectItem>{Object.entries(types).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="h-9 w-[150px] rounded-[10px] bg-white" data-testid="radar-country-filter"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All countries</SelectItem>{countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={confidence} onValueChange={setConfidence}>
            <SelectTrigger className="h-9 w-[160px] rounded-[10px] bg-white" data-testid="radar-confidence-filter"><SelectValue placeholder="Confidence" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Any confidence</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
          </Select>
          <Segmented items={DAYS} value={days} onChange={setDays} testIdPrefix="radar-days" />
        </div>
        <SearchField value={q} onChange={setQ} placeholder="Search signals, companies, projects…" className="w-full xl:w-[320px]" testId="radar-search-input" />
      </div>
      {active.length ? (
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          {active.map((a) => <button key={a.k} type="button" onClick={a.clear} className="inline-flex items-center gap-1 rounded-full border border-black/[0.08] bg-white px-2.5 py-1 text-[12px] hover:border-black/[0.16]" data-testid={`radar-${a.k}-filter-chip`}>{a.label} <X className="h-3 w-3 text-muted-foreground" /></button>)}
          <button type="button" onClick={() => { setType("all"); setCountry("all"); setConfidence("all"); setDays("0"); }} className="text-[12px] text-muted-foreground hover:text-foreground" data-testid="radar-clear-filters">Clear all</button>
        </div>
      ) : null}

      {!data ? (
        <div className="surface-flat divide-y divide-black/[0.05]">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="flex gap-4 px-5 py-4"><Skeleton className="h-9 w-9 rounded-lg" /><div className="flex-1"><Skeleton className="h-4 w-1/3" /><Skeleton className="mt-2 h-3 w-1/2" /></div><Skeleton className="h-6 w-16" /></div>)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Radar} title={q ? `No signals match “${q}”` : "No signals in this view"} description="Widen the time window or clear filters to see more of the market." action={<Button variant="outline" onClick={() => { setQ(""); setType("all"); setCountry("all"); setConfidence("all"); setDays("0"); }}>Clear filters</Button>} testId="radar-empty" />
      ) : isDesktop ? (
        <div className="surface-flat overflow-hidden">
          <Table data-testid="radar-signals-table">
            <TableHeader className="bg-[hsl(40_12%_98%)]">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[32%] text-[10.5px] uppercase tracking-[0.12em]">Project · Company</TableHead>
                <TableHead className="text-[10.5px] uppercase tracking-[0.12em]">Signal</TableHead>
                <TableHead className="w-[90px] text-[10.5px] uppercase tracking-[0.12em]">Date</TableHead>
                <TableHead className="w-[130px] text-[10.5px] uppercase tracking-[0.12em]">Confidence</TableHead>
                <TableHead className="w-[150px] text-[10.5px] uppercase tracking-[0.12em]">Timing</TableHead>
                <TableHead className="w-[110px] text-right text-[10.5px] uppercase tracking-[0.12em]">Score</TableHead>
                <TableHead className="w-[36px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.signal.id} onClick={() => open(r)} tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(r); } }} className={cn("group cursor-pointer hover:bg-[hsl(var(--muted)/0.55)] focus-visible:outline-none focus-visible:bg-[hsl(var(--muted)/0.55)]", selected === r.signal.id && "bg-[hsl(var(--muted)/0.4)]")} data-testid="radar-signal-row">
                  <TableCell className="py-3">
                    <div className="flex items-center gap-3">
                      <CompanyLogo name={r.company.name} domain={r.company.website} size="sm" />
                      <div className="min-w-0">
                        <div className="truncate text-[13.5px] font-semibold tracking-[-0.01em]" data-testid="radar-row-project">{r.project_name}</div>
                        <div className="truncate text-[12px] text-muted-foreground">{r.company.name} · {r.company.segment} · {r.company.country}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground"><EvidenceLabel kind="signal" compact />{r.signal_label}</div>
                    <div className="mt-0.5 line-clamp-1 text-[13px]" data-testid="radar-row-signal">{r.signal.title}</div>
                    <SourceLine signal={r.signal} showDate={false} className="mt-0.5 text-[11.5px]" />
                  </TableCell>
                  <TableCell className="py-3 font-mono text-[12px] tabular text-muted-foreground">{fmtDate(r.signal.detected_date)}</TableCell>
                  <TableCell className="py-3"><ConfidenceChip level={r.confidence} short /></TableCell>
                  <TableCell className="py-3"><TimingChip timing={r.timing} /><div className="text-[11.5px] text-muted-foreground">{phaseLabel(r.phase)}</div></TableCell>
                  <TableCell className="py-3 text-right"><ScorePill score={r.score} /></TableCell>
                  <TableCell className="py-3 pr-4"><ArrowUpRight className="h-4 w-4 text-muted-foreground/50 transition-colors group-hover:text-primary" data-testid="radar-signal-open-details" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="space-y-3" data-testid="radar-signals-list">
          {rows.map((r) => (
            <button key={r.signal.id} type="button" onClick={() => open(r)} className="surface-flat w-full px-4 py-4 text-left" data-testid="radar-signal-row">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3"><CompanyLogo name={r.company.name} domain={r.company.website} size="sm" /><div className="min-w-0"><div className="truncate text-[13.5px] font-semibold">{r.project_name}</div><div className="truncate text-[12px] text-muted-foreground">{r.company.name} · {r.company.country}</div></div></div>
                <ScorePill score={r.score} />
              </div>
              <div className="mt-3 text-[13px] leading-snug">{r.signal.title}</div>
              <div className="mt-2 flex flex-wrap items-center gap-2"><ConfidenceChip level={r.confidence} short /><TimingChip timing={r.timing} phase={r.phase} /><span className="font-mono text-[11.5px] text-muted-foreground">{fmtDate(r.signal.detected_date)}</span></div>
              <div className="mt-2 text-[12px] text-muted-foreground">Est. <ValueRange label={r.value_label} /> · {r.recommended_action}</div>
            </button>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
